<template>
  <div class="budget-page">
    <!-- 로딩 컴포넌트 -->
    <LodingComponent :is-visible="isInitialLoading" text="예산 데이터 확인 중..." />
    
    <!-- 예산 데이터가 없을 때: Q&A 위저드 표시 -->
    <PgmBudgetStarter 
      v-if="!isInitialLoading && showStarter"
      :year="currentYear"
      :suga-list="sugaList"
      :suga-year="sugaYear"
      :suga-is-fallback="sugaIsFallback"
      :suga-error="sugaError"
      :suga-loading="sugaLoading"
      :tax-data="taxData"
      :soji-data="sojiData"
      :tax-loading="taxLoading"
      :tax-error="taxError"
      :budget-class="budgetClass"
      @complete="handleBudgetComplete"
      @cancel="handleStarterCancel"
      @year-change="handleYearChange"
    />
    
    <!-- 예산 데이터가 있을 때: 메인 예산 편집 화면 표시 -->
    <PgmBudgetMain 
      v-if="!isInitialLoading && !showStarter"
      ref="budgetMainRef"
      :year="currentYear"
      :suga-list="sugaList"
      :suga-year="sugaYear"
      :suga-is-fallback="sugaIsFallback"
      :tax-data="taxData"
      :soji-data="sojiData"
      @open-starter="openStarter"
      @year-change="handleYearChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import PgmBudgetStarter from '@/components/fna/PgmBudgetStarter.vue';
import PgmBudgetMain from '@/components/fna/PgmBudgetMain.vue';

const store = useStore();

// 수가 인터페이스
interface SugaData {
  suga_idx: number;
  suga_biz_gubun: string;
  suga_year: string;
  suga_rank: string;
  suga_money: number;
  suga_bonin: number;
  suga_wdate: string;
}

// 세금/보험 요율 인터페이스
interface TaxData {
  tr_idx?: number;
  tr_year?: string;
  tr_time_pay?: number;   // 최저시급
  tr_yeon?: number;       // 국민연금 요율
  tr_gun?: number;        // 건강보험 요율
  tr_go?: number;         // 고용보험 요율
  tr_jang?: number;       // 장기요양보험 요율 (건강보험 기준)
  tr_sil?: number;        // 실업급여 요율
  tr_san?: number;        // 산재보험 요율
}

// 소득세 테이블 인터페이스
interface SojiData {
  income1: number;        // 최소 급여 (천원 단위)
  income2: number;        // 최대 급여 (천원 단위)
  depe_1: number;         // 부양가족 1명 소득세
  depe_2?: number;
  depe_3?: number;
  depe_4?: number;
  depe_5?: number;
}

// 참조
const budgetMainRef = ref<InstanceType<typeof PgmBudgetMain> | null>(null);

// 상태
const isInitialLoading = ref(true); // 초기 로딩 상태
const showStarter = ref(false);
const currentYear = ref(store.state.lebYear || new Date().getFullYear().toString());
const hasBudgetData = ref(false);
const budgetClass = ref<string>(''); // 추경차수: '', '1', '2' 등

// 수가 관련 상태
const sugaList = ref<SugaData[]>([]);
const sugaYear = ref<string>('');
const sugaIsFallback = ref(false);
const sugaError = ref<string>('');
const sugaLoading = ref(false);

// 180분 기준 수가
const suga180 = computed(() => sugaList.value.find(s => s.suga_rank === '180분') || null);

// 세금/보험 요율 관련 상태
const taxData = ref<TaxData>({});
const sojiData = ref<SojiData[]>([]);
const taxLoading = ref(false);
const taxError = ref<string>('');

// 예산 데이터 존재 여부 확인
const checkBudgetExists = async () => {
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    if (!sisulCode) {
      showStarter.value = true;
      return;
    }

    const response = await axios.get('/api/budget/exists', {
      params: {
        sisulCode,
        year: currentYear.value
      }
    });

    hasBudgetData.value = response.data.exists;
    budgetClass.value = response.data.budgetClass || ''; // 추경차수 가져오기
    showStarter.value = !hasBudgetData.value;
  } catch (error) {
    console.error('예산 데이터 확인 오류:', error);
    // 오류 시 스타터 표시
    showStarter.value = true;
    budgetClass.value = '';
  } finally {
    isInitialLoading.value = false;
  }
};

// 연도 변경 핸들러
const handleYearChange = async (newYear: string) => {
  currentYear.value = newYear;
  store.commit('setLebYear', newYear);
  
  // 예산 존재 여부 다시 확인
  isInitialLoading.value = true;
  await checkBudgetExists();
};

// Q&A 완료 핸들러
const handleBudgetComplete = async (budgetData: any) => {
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    if (!sisulCode) {
      console.error('시설 코드가 없습니다.');
      return;
    }

    // 예산 데이터 저장 (프론트엔드에서 계산된 budgetEntries 직접 전달)
    const response = await axios.post('/api/budget/create-from-qa', {
      sisulCode,
      year: budgetData.year,
      budgetEntries: budgetData.budgetEntries
    });

    if (response.data.success) {
      hasBudgetData.value = true;
      showStarter.value = false;
      
      // 메인 컴포넌트 데이터 새로고침
      setTimeout(() => {
        if (budgetMainRef.value) {
          // 메인 컴포넌트의 fetchFinanceCodes 호출
          (budgetMainRef.value as any).fetchFinanceCodes?.();
        }
      }, 100);
    }
  } catch (error) {
    console.error('예산 생성 오류:', error);
  }
};

// 스타터 취소 핸들러
const handleStarterCancel = () => {
  if (hasBudgetData.value) {
    showStarter.value = false;
  }
};

// 스타터 열기 (메인에서 호출)
const openStarter = () => {
  showStarter.value = true;
};

// 수가 데이터 로드
const loadSugaData = async () => {
  sugaLoading.value = true;
  sugaError.value = '';
  
  try {
    const response = await axios.get(`/api/calc/suga/${currentYear.value}`);
    
    if (response.data.success) {
      sugaList.value = response.data.data || [];
      sugaYear.value = response.data.year || currentYear.value;
      sugaIsFallback.value = response.data.isFallback || false;
      
      // 180분 수가가 없으면 에러 (suga_rank === '180분')
      if (!suga180.value) {
        sugaError.value = `${sugaYear.value}년 수가 데이터에 '180분' 기준 수가가 없습니다.`;
      }
    }
  } catch (error: any) {
    console.error('수가 데이터 로드 오류:', error);
    
    if (error.response?.status === 404) {
      sugaError.value = error.response.data?.message || '수가 데이터가 없습니다.';
    } else {
      sugaError.value = '수가 데이터를 불러오는 중 오류가 발생했습니다.';
    }
    
    sugaList.value = [];
    sugaYear.value = '';
  } finally {
    sugaLoading.value = false;
  }
};

// 세금/보험 요율 데이터 로드
const loadTaxData = async () => {
  taxLoading.value = true;
  taxError.value = '';
  
  try {
    const response = await axios.get(`/api/calc/tax/${currentYear.value}`);
    
    if (response.data.success) {
      taxData.value = response.data.data.taxRate || {};
      sojiData.value = response.data.data.soji || [];
      
      if (!taxData.value || !taxData.value.tr_time_pay) {
        taxError.value = `${currentYear.value}년 세금/보험 요율 데이터가 없습니다.`;
      }
    }
  } catch (error: any) {
    console.error('세금 데이터 로드 오류:', error);
    taxError.value = '세금/보험 요율 데이터를 불러오는 중 오류가 발생했습니다.';
    taxData.value = {};
    sojiData.value = [];
  } finally {
    taxLoading.value = false;
  }
};

// 연도 변경 시 수가, 세금 다시 로드
watch(currentYear, () => {
  loadSugaData();
  loadTaxData();
});

// 초기화
onMounted(async () => {
  await Promise.all([loadSugaData(), loadTaxData()]);
  checkBudgetExists();
});
</script>

<style lang="scss" scoped>
.budget-page {
  min-height: calc(100vh - 60px);
  background: #f5f7fa;
}
</style>
